import { useEffect, useState, useMemo } from "react";
import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
import { showAlert, showErrorAlert } from "../../../components/molecules/ShowAlert";
import { getAllRegistros } from "../../../services/registro.service";
import { getAllEstudiantes } from "../../../services/estudiante.service";
import { getAllInscripciones } from "../../../services/inscripcion.service";
import { getAllTipoAdmisiones } from "../../../services/tipoAdmision.service";
import { getAllProgramas } from "../../../services/programa.service";
import { getAllGrupos } from "../../../services/grupo.service";
import { getAllModulos } from "../../../services/modulo.service";
import { getAllIdiomas } from "../../../services/idioma.service";
import { EstudianteReprobadoList } from "../../../modules/estudiante/EstudianteReprobadoList";

const parsearFechaApi = (fechaString) => {
  if (!fechaString) return null;
  return new Date(fechaString);
};
export const EstudiantesReprobadosPage = () => {
  const [mes, setMes] = useState("");
  const [año, setAño] = useState("");
  const [idiomaSeleccionado, setIdiomaSeleccionado] = useState("");
  const [reprobados, setReprobados] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [listaIdiomas, setListaIdiomas] = useState([]);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    cargarDatos();
  }, [mes, año]);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const [registros, estudiantes, inscripciones, modulos, idiomas, grupos, tiposAdmision, programas] = await Promise.all([
        getAllRegistros(),
        getAllEstudiantes(),
        getAllInscripciones(),
        getAllModulos().catch(() => []),
        getAllIdiomas().catch(() => []),
        getAllGrupos().catch(() => []),
        getAllTipoAdmisiones().catch(() => []),
        getAllProgramas().catch(() => []),
      ]);

      // Crear diccionarios de una vez para acceso O(1)
      const moduloById = {};
      for (const m of (modulos || [])) {
        if (m?.idModulo) moduloById[m.idModulo] = m;
      }

      const idiomaById = {};
      for (const i of (idiomas || [])) {
        if (i?.idIdioma) idiomaById[i.idIdioma] = i;
      }
      setListaIdiomas(idiomas || []);

      const dicEstudiantes = {};
      for (const est of estudiantes) {
        const idEst = est.estudiante?.idEstudiante?.trim();
        if (idEst) {
          dicEstudiantes[idEst] = est;
        }
      }

      const grupoById = {};
      for (const g of (grupos || [])) {
        if (g?.idGrupo) grupoById[g.idGrupo] = g;
      }

      const tipoAdmisionById = {};
      for (const t of (tiposAdmision || [])) if (t?.idTipoAdmision) tipoAdmisionById[t.idTipoAdmision] = t;
      
      const programaById = {};
      for (const p of (programas || [])) if (p?.idPrograma) programaById[p.idPrograma] = p;

      // Crear diccionario de inscripciones por estudiante para acceso O(1)
      const inscripcionByEstudiante = {};
      for (const ins of inscripciones) {
        if (ins?.idEstudiante) {
          inscripcionByEstudiante[ins.idEstudiante] = ins;
        }
      }

      // Filtrar reprobados y transformar en una sola pasada
      const reprobadosTransformados = [];
      for (const registro of registros) {
        if (!registro.estado) continue;
        const estado = registro.estado.trim().toLowerCase();
        if (estado !== "reprobado") continue;
        
        const fechaApi = parsearFechaApi(registro.fechaReprobado);
        if (!fechaApi) continue;

        const mesBD = fechaApi.getMonth() + 1; 
        const añoBD = fechaApi.getFullYear();

        const coincideMes = mes ? mesBD === Number(mes) : true;
        const coincideAño = año ? añoBD === Number(año) : true;

        if (!coincideMes || !coincideAño) continue;

        const idEst = registro.idEstudiante?.trim();
        const estudianteEncontrado = dicEstudiantes[idEst];

        if (!estudianteEncontrado) {
          console.warn("No se encontró el estudiante con ID:", idEst);
          continue;
        }

        const tipoAdmisionId = estudianteEncontrado.estudiante?.idTipoAdmision;
        const tipoAdmisionNombre = tipoAdmisionId ? (tipoAdmisionById[tipoAdmisionId]?.nombre || "N/A") : "N/A";

        const inscripcionEncontrada = inscripcionByEstudiante[idEst];
        const nombrePrograma = inscripcionEncontrada?.idPrograma ? (programaById[inscripcionEncontrada.idPrograma]?.nombre || "N/A") : "N/A";
        
        let grupoData = { nombre: "N/A", modulo: { nombre: "N/A" } };
        let idiomaData = { id: null, nombre: "N/A" };
        
        if (registro.idGrupo) {
          const grupoInfo = grupoById[registro.idGrupo];
          if (grupoInfo) {
            grupoData.nombre = grupoInfo.nombre;
            if (grupoInfo.idModulo) {
              const moduloInfo = moduloById[grupoInfo.idModulo];
              if (moduloInfo) {
                grupoData.modulo = { nombre: moduloInfo.nombre };
                const idioma = idiomaById[moduloInfo.idIdioma];
                if (idioma) {
                  idiomaData = { id: idioma.idIdioma, nombre: idioma.nombre };
                }
              }
            }
          }
        }

        reprobadosTransformados.push({
          persona: estudianteEncontrado.persona,
          estudiante: {
            ...estudianteEncontrado.estudiante,
            tipoAdmision: tipoAdmisionNombre,
          },
          inscripcion: {
            programa: { nombre: nombrePrograma },
          },
          registro: {
            ...registro,
            grupo: grupoData,
            fechaReprobado: registro.fechaReprobado,
            tipoCongelacion: registro.tipoCongelacion || "N/A",
          },
          idioma: idiomaData,
        });
      }

      setReprobados(reprobadosTransformados);
    } catch (error) {
      console.error("Error cargando datos:", error);
      showErrorAlert({
        text: "Error al cargar los registros de estudiantes reprobados.",
        icon: "error",
      });
    } finally {
      setCargando(false);
    }
  };

  const handleDelete = (idEstudiante) => {
    setReprobados((prev) => prev.filter((c) => c.estudiante.idEstudiante !== idEstudiante));
  };

  // Memoizar el filtrado para evitar recálculos innecesarios
  const reprobadosFiltrados = useMemo(() => {
    if (!busqueda && !idiomaSeleccionado) {
      return reprobados;
    }

    const texto = busqueda.toLowerCase();
    const idiomaIdSeleccionado = idiomaSeleccionado ? String(idiomaSeleccionado) : null;

    return reprobados.filter((est) => {
      // Filtro por idioma primero (más rápido)
      if (idiomaIdSeleccionado) {
        const idiomaId = est.idioma?.id ? String(est.idioma.id) : "";
        if (idiomaId !== idiomaIdSeleccionado) return false;
      }

      // Si no hay búsqueda de texto, solo retornar si pasó el filtro de idioma
      if (!texto) return true;

      // Filtro por texto (solo si hay búsqueda)
      const nombre = est.persona?.nombre?.toLowerCase() || "";
      const apellido = est.persona?.apellido?.toLowerCase() || "";
      const celular = est.persona?.celular?.toLowerCase() || "";
      const tipoAdmision = est.estudiante?.tipoAdmision?.toLowerCase() || "";
      const programa = est.inscripcion?.programa?.nombre?.toLowerCase() || "";
      const grupo = est.registro?.grupo?.nombre?.toLowerCase() || "";
      const modulo = est.registro?.grupo?.modulo?.nombre?.toLowerCase() || "";

      return (
        nombre.includes(texto) ||
        apellido.includes(texto) ||
        celular.includes(texto) ||
        tipoAdmision.includes(texto) ||
        programa.includes(texto) ||
        grupo.includes(texto) ||
        modulo.includes(texto)
      );
    });
  }, [reprobados, busqueda, idiomaSeleccionado]);
  

  return (
    <ContenedorPageTemplate titulo="Estudiantes Reprobados">
      <div className="flex flex-col justify-start items-start mb-3">
        <p>
          <span className="text-lg font-semibold">
            Total de reprobados: {reprobadosFiltrados.length}
          </span>
        </p>
      </div>
      <div className="flex gap-4 py-3">
        <select
          value={mes}
          onChange={(e) => setMes(e.target.value)}
          disabled={cargando}
          className="border rounded-lg p-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">Todos los meses</option>
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i + 1} value={i + 1}>
              {new Date(0, i).toLocaleString("es", { month: "long" })}
            </option>
          ))}
        </select>
        <input
          type="number"
          value={año}
          onChange={(e) => setAño(e.target.value)}
          placeholder="Todos los años"
          disabled={cargando}
          className="border rounded-lg p-2 disabled:opacity-50 disabled:cursor-not-allowed"
          min="2000"
          max={new Date().getFullYear() + 5}
        />
        <select
          value={idiomaSeleccionado}
          onChange={(e) => setIdiomaSeleccionado(e.target.value)}
          disabled={cargando}
          className="border rounded-lg p-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">Todos los idiomas</option>
          {listaIdiomas.map((idioma) => (
            <option key={idioma.idIdioma} value={idioma.idIdioma}>
              {idioma.nombre}
            </option>
          ))}
        </select>
      </div>
      <div className="flex gap-4 py-3">
        <input
          type="text"
          placeholder="Buscar por nombre, apellido, teléfono, tipo de admisión, programa, grupo o módulo"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          disabled={cargando}
          className="flex-1 text-black border shadow-sm rounded-xl py-3 px-4 focus:ring-1 
                     focus:border-blue-500 focus:ring-blue-300 transition-colors 
                     hover:border-blue-500 border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
      {cargando ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-lg text-gray-600 font-semibold">Cargando estudiantes reprobados...</p>
          <p className="text-sm text-gray-500 mt-2">Esto puede tomar unos segundos</p>
        </div>
      ) : (
        <EstudianteReprobadoList estudiantes={reprobadosFiltrados} onDelete={handleDelete} />
      )}
    </ContenedorPageTemplate>
  );
};
